import { Moon, Sun } from "lucide-react";

import { useTheme } from "@/contexts/ThemeContext";
import { cn } from "@/lib/utils";

/**
 * Theme switcher. Relay is dark-first; this lets a user flip to the light
 * variant. Renders as a compact pill-with-knob so the current theme reads at
 * a glance even when the sidebar is collapsed (icon-only) mode.
 */
export function ThemeToggle({
  collapsed = false,
  className,
}: {
  collapsed?: boolean;
  className?: string;
}) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
        className={cn(
          "flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
          className,
        )}
      >
        {isDark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggleTheme}
      role="switch"
      aria-checked={isDark}
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      className={cn(
        "group relative flex h-9 w-full items-center gap-2 rounded-lg border border-border bg-secondary/60 px-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground",
        className,
      )}
    >
      <span
        className={cn(
          "flex h-6 w-6 items-center justify-center rounded-md transition-all duration-300",
          isDark
            ? "bg-card text-gold shadow-sm"
            : "bg-transparent",
        )}
      >
        <Moon className="h-3.5 w-3.5" />
      </span>
      <span
        className={cn(
          "flex h-6 w-6 items-center justify-center rounded-md transition-all duration-300",
          !isDark
            ? "bg-card text-gold shadow-sm"
            : "bg-transparent",
        )}
      >
        <Sun className="h-3.5 w-3.5" />
      </span>
      <span className="ml-0.5 truncate">{isDark ? "Dark" : "Light"}</span>
    </button>
  );
}
